/**
 * Sovereign Audit — simplified calculator math.
 *
 * Source of truth: /app/tmp/greengold/GreenGold_Website_Build_Package/reference/Sovereign_Shakti_Calculator_ARD.html
 *
 * This is an intentionally simplified take on the ARD. The ARD covers a full
 * five-stage design studio; the /audit prompt asks for a friendly 3-step flow
 * that ends in an RFP. We keep the same assumption values and the same
 * shape of the outputs (system size, payback, diesel savings, landed cost)
 * so the numbers stay honest and comparable to a full engagement.
 */

// ---- Assumption constants (surface these in the assumptions footnote) ----
export const ASSUMPTIONS = {
  solarCUF: 0.17, // 17% capacity utilisation
  hoursInYear: 8760,
  sqmPerKwp: 9.29, // ~100 sqft / 10 kWp
  daytimeLoadCoveragePct: 0.8,
  bessDischargeHours: 4, // default BESS discharge duration
  bessRoundTripEff: 0.9,
  dieselLitresPerKwh: 0.3, // industrial DG efficiency
  solarInstallCostPerKwp: 40000, // ₹
  workingDaysPerMonth: 25,
  systemLifeYears: 10,
  omPctOfCapex: 0.03, // combined solar+bess O&M as % capex/year
  co2PerMwh: 0.85, // tonnes CO2 per MWh of grid displacement (informational)
  gcppFloorInrPerKwh: 4.0,
  gcppCeilInrPerKwh: 5.5,
};

// BESS capex per kWh — non-linear tiering from the ARD.
export function bessCostPerKwh(kwh) {
  if (kwh <= 100) return 22000;
  if (kwh <= 250) return 20000;
  if (kwh <= 500) return 18000;
  if (kwh <= 1000) return 15000;
  return 12000;
}

export const LOAD_PROFILES = {
  industrial: { label: "Industrial", peakLoadKwPerSqm: 0.14, dayHours: 10 },
  commercial: { label: "Commercial", peakLoadKwPerSqm: 0.08, dayHours: 10 },
  institutional: { label: "Institutional", peakLoadKwPerSqm: 0.06, dayHours: 10 },
  "captive-seeker": {
    label: "Captive-Seeker (industry pursuing GCPP)",
    peakLoadKwPerSqm: 0.14,
    dayHours: 12,
  },
};

/**
 * Compute the peak load from the two possible inputs. Prefer the explicit
 * kW input if given; otherwise estimate from roof area × load-profile factor.
 */
export function estimatePeakLoadKw({ peakLoadKw, roofSqm, loadProfile }) {
  if (peakLoadKw && peakLoadKw > 0) return peakLoadKw;
  const profile = LOAD_PROFILES[loadProfile] || LOAD_PROFILES.industrial;
  return Math.max(1, (roofSqm || 0) * profile.peakLoadKwPerSqm);
}

/**
 * Full compute given Step 1 + Step 2 inputs. Everything client-side; returns
 * a plain object that the results view (and the RFP payload) can consume.
 */
export function computeAudit(inputs) {
  const {
    tariff = 10,
    dgHours = 0,
    dgCostPerLitre = 95,
    location = "",
    pincode = "",
    loadProfile = "industrial",
    roofSqm = 0,
    peakLoadKw = 0,
    existingSolar = false,
  } = inputs;

  const profile = LOAD_PROFILES[loadProfile] || LOAD_PROFILES.industrial;
  const loadKw = estimatePeakLoadKw({ peakLoadKw, roofSqm, loadProfile });

  // ---- Sizing ----
  // SB (Solar + BESS): size solar by both space cap and load cap; BESS covers
  // 4h of peak load.
  // BO (BESS-only): no new solar; BESS = 4h × peak load.
  const solarBySpace = (roofSqm || 0) / ASSUMPTIONS.sqmPerKwp;
  const solarByLoad = loadKw * ASSUMPTIONS.daytimeLoadCoveragePct;
  const solarKwp = existingSolar
    ? 0
    : Math.max(1, Math.min(solarBySpace > 0 ? solarBySpace : solarByLoad, solarByLoad));
  const bessKwh = Math.max(
    50,
    Math.round(loadKw * ASSUMPTIONS.bessDischargeHours)
  );
  const systemType = existingSolar ? "BO" : "SB";

  // ---- Capex ----
  const solarCapex = solarKwp * ASSUMPTIONS.solarInstallCostPerKwp;
  const bessCapex = bessKwh * bessCostPerKwh(bessKwh);
  const totalCapex = Math.round(solarCapex + bessCapex);

  // ---- Annual energy ----
  const annualSolarKwh = Math.round(
    solarKwp * ASSUMPTIONS.solarCUF * ASSUMPTIONS.hoursInYear
  );
  // Assume BESS cycles once per working day
  const annualBessKwh = Math.round(
    bessKwh * ASSUMPTIONS.workingDaysPerMonth * 12 * ASSUMPTIONS.bessRoundTripEff
  );
  const annualDisplacedKwh = annualSolarKwh + annualBessKwh;

  // ---- Diesel savings ----
  // Assume DG carries peakLoadKw when running. Litres = kWh × 0.3.
  const annualDgKwh = dgHours * 365 * loadKw;
  const dieselLitresSaved = Math.round(
    annualDgKwh * ASSUMPTIONS.dieselLitresPerKwh
  );
  const dieselInrSaved = Math.round(dieselLitresSaved * dgCostPerLitre);

  // ---- Landed cost ----
  const currentBillInr = Math.round(
    annualDisplacedKwh * tariff + dieselInrSaved
  );
  const currentLandedInrPerKwh =
    annualDisplacedKwh > 0
      ? Math.round((currentBillInr / annualDisplacedKwh) * 100) / 100
      : tariff;
  const annualOmInr = totalCapex * ASSUMPTIONS.omPctOfCapex;
  const annualisedCapex = totalCapex / ASSUMPTIONS.systemLifeYears;
  const projectedAnnualCost = annualisedCapex + annualOmInr;
  const projectedLandedInrPerKwh =
    annualDisplacedKwh > 0
      ? Math.round((projectedAnnualCost / annualDisplacedKwh) * 100) / 100
      : projectedAnnualCost;

  // ---- Payback ----
  const annualBenefit =
    annualSolarKwh * tariff + annualBessKwh * (tariff * 0.4) + dieselInrSaved;
  // NB: BESS savings come from arbitrage/peak-shaving — conservatively 40%
  // of the flat tariff. Prevents wild over-optimism.
  const paybackYears =
    annualBenefit > 0
      ? Math.round((totalCapex / annualBenefit) * 10) / 10
      : Infinity;

  return {
    // sizing
    systemType,
    solarKwp: Math.round(solarKwp * 10) / 10,
    bessKwh,
    systemSizeKwh: bessKwh, // headline for the ≥250 kWh gate
    loadKw: Math.round(loadKw * 10) / 10,
    // financials
    totalCapex,
    annualSolarKwh,
    annualBessKwh,
    annualDisplacedKwh,
    dieselLitresSaved,
    dieselInrSaved,
    currentLandedInrPerKwh,
    projectedLandedInrPerKwh,
    paybackYears,
    // echo back the inputs for the RFP payload
    inputs: {
      tariff,
      dgHours,
      dgCostPerLitre,
      location,
      pincode,
      loadProfile,
      roofSqm,
      peakLoadKw,
      existingSolar,
    },
  };
}

/**
 * GCPP — Group Captive Power Project variant math.
 *
 * Per `reference/Green_Gold_Grid_Tabs.html`, the pillar-two numbers are:
 *   ₹8–12/kWh grid tariff → ₹4–5.50/kWh landed captive cost, when the
 *   industry holds ≥26% equity and consumes ≥51% of the output.
 *
 * We model this as a linear interpolation between the floor and the ceiling
 * scaled by how much the actual equity/consumption exceeds the 26%/51% minimums.
 */
export function computeGcpp({ tariff, equityPct, consumptionPct }) {
  const eqExcess = Math.max(0, equityPct - 26);
  const consExcess = Math.max(0, consumptionPct - 51);
  // Fully-optimised (eq→60, cons→100) lands at the floor; the minimums
  // (26/51) land at the ceiling. Linear blend between.
  const optimisationScore = Math.min(
    1,
    (eqExcess / 34 + consExcess / 49) / 2
  );
  const landed =
    ASSUMPTIONS.gcppCeilInrPerKwh -
    optimisationScore *
      (ASSUMPTIONS.gcppCeilInrPerKwh - ASSUMPTIONS.gcppFloorInrPerKwh);
  const savingsPerKwh = Math.max(0, tariff - landed);
  return {
    equityPct,
    consumptionPct,
    landedInrPerKwh: Math.round(landed * 100) / 100,
    savingsPerKwh: Math.round(savingsPerKwh * 100) / 100,
    savingsPct: Math.round((savingsPerKwh / tariff) * 100),
    qualifies: equityPct >= 26 && consumptionPct >= 51,
  };
}
